import { Injectable } from "@angular/core";

export interface ReasoningStepInfo {
  stepLabel: string;
  description: string;
}

export type MessageClassification =
  | { type: "reasoning"; info: ReasoningStepInfo }
  | { type: "message" }
  | { type: "silent" }
  | { type: "pending" };

/**
 * Classifies assistant messages by inspecting their content.
 * Classification is based solely on content — tool calls do not affect visibility.
 *
 * The backend sends reasoning messages as JSON with `is_reasoning: true`.
 * Detection works on partial JSON during streaming so reasoning steps
 * display progressively as chunks arrive.
 */
@Injectable({ providedIn: "root" })
export class ReasoningStepService {
  /**
   * Returns one of four classifications:
   * - message:   plain text to show the user
   * - reasoning: JSON with is_reasoning=true (or a chunk building toward it)
   * - pending:   first bytes of a reasoning JSON, not yet classifiable
   * - silent:    no displayable content (empty, tool-only, internal JSON)
   */
  classifyMessage(content: unknown): MessageClassification {
    if (Array.isArray(content)) {
      const hasText = content.some((p: any) => p?.type === "text" && p.text?.trim());
      const hasBlocks = content.some((p: any) => p?.type === "non_standard");
      return hasText || hasBlocks ? { type: "message" } : { type: "silent" };
    }

    const text = typeof content === "string" ? content.trim() : "";

    if (!text) return { type: "silent" };
    if (!text.startsWith("{")) return { type: "message" };

    // JSON — determine if it is a reasoning step
    const isReasoning = this.detectIsReasoning(text);

    if (isReasoning === true) {
      return {
        type: "reasoning",
        info: {
          stepLabel: this.extractField(text, "step_label") ?? "Processing…",
          description: this.extractField(text, "reasoning") ?? this.extractField(text, "reason") ?? "",
        },
      };
    }

    if (isReasoning === false) return { type: "silent" };

    // null — field not yet in the stream; only show pending if it looks like reasoning JSON
    return this.looksLikeReasoningJson(text) ? { type: "pending" } : { type: "silent" };
  }

  /**
   * Reads is_reasoning from a complete or partial JSON string.
   * Returns null when the field has not arrived yet.
   */
  private detectIsReasoning(text: string): boolean | null {
    try {
      return !!(JSON.parse(text) as Record<string, unknown>).is_reasoning;
    } catch {
      const match = text.match(/"is_reasoning"\s*:\s*(true|false)/);
      return match ? match[1] === "true" : null;
    }
  }

  /**
   * Extracts a string field from a complete or partial JSON string.
   * Appends "…" when the value is still streaming.
   */
  private extractField(text: string, field: string): string | null {
    try {
      const val = (JSON.parse(text) as Record<string, unknown>)[field];
      return typeof val === "string" ? val : null;
    } catch {
      const escaped = field.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const complete = text.match(new RegExp(`"${escaped}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`));
      if (complete) return complete[1];
      const partial = text.match(new RegExp(`"${escaped}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)+)`));
      return partial ? partial[1] + "…" : null;
    }
  }

  private looksLikeReasoningJson(text: string): boolean {
    return /^{\s*"(?:is_r|reas)/.test(text);
  }
}
